import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { CreditCard, Loader2, CheckCircle2, AlertCircle, ArrowLeft, LogIn } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function UpdatePaymentMethodPage() {
  const { user, token, loading: authLoading, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [status, setStatus] = useState('idle'); // idle | opening | retrying | restored | error
  const [error, setError] = useState('');

  // Redirect unauthenticated visitors to login with a return URL. This handles
  // the common case of tapping the "Update Card" button in an email while not
  // logged in on that browser (Gmail in-app WebView, incognito, cleared PWA, etc.)
  useEffect(() => {
    if (authLoading) return; // wait for AuthContext to finish hydrating
    if (!token || !user) {
      const returnTo = encodeURIComponent('/subscription/update-payment');
      navigate(`/login?next=${returnTo}&reason=update_card`, { replace: true });
    }
  }, [authLoading, token, user, navigate]);

  // If we came back from the Billing Portal with ?updated=1, immediately retry the outstanding invoice
  useEffect(() => {
    if (!token || !user) return;
    if (params.get('updated') === '1') {
      retryOutstandingInvoice();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, user]);

  const openBillingPortal = async () => {
    setStatus('opening');
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/subscription/update-payment-method`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      let data = null;
      try { data = await res.json(); } catch (_) { /* non-JSON body */ }
      if (res.ok && data && data.url) {
        window.location.href = data.url;
        return;
      }
      // Auth expired mid-session — bounce to login
      if (res.status === 401) {
        const returnTo = encodeURIComponent('/subscription/update-payment');
        navigate(`/login?next=${returnTo}&reason=session_expired`, { replace: true });
        return;
      }
      setStatus('error');
      setError((data && typeof data.detail === 'string') ? data.detail : 'Could not open update-card page');
    } catch (e) {
      setStatus('error');
      setError('Network error opening update-card page. Please check your connection and try again.');
    }
  };

  const retryOutstandingInvoice = async () => {
    setStatus('retrying');
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/subscription/retry-payment`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      let data = null;
      try { data = await res.json(); } catch (_) { /* non-JSON body */ }
      if (res.ok) {
        // Give the webhook a moment to fire, then refresh the user state.
        // If the invoice paid, subscription_status will now be "active" and the banner disappears.
        await new Promise((r) => setTimeout(r, 2000));
        if (refreshUser) await refreshUser();
        setStatus('restored');
        return;
      }
      if (res.status === 401) {
        const returnTo = encodeURIComponent('/subscription/update-payment');
        navigate(`/login?next=${returnTo}&reason=session_expired`, { replace: true });
        return;
      }
      setStatus('error');
      setError((data && typeof data.detail === 'string') ? data.detail : 'Payment retry failed');
    } catch (e) {
      setStatus('error');
      setError('Network error retrying payment. Please check your connection and try again.');
    }
  };

  // While AuthContext is still hydrating, or while the redirect-to-login effect
  // hasn't fired yet, show a lightweight loader instead of the button (prevents
  // clicks that would send `Bearer null` and confuse mobile users).
  if (authLoading || !token || !user) {
    return (
      <div className="min-h-[70vh] bg-gradient-to-br from-rose-50 to-orange-50 py-12 px-4 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center" data-testid="update-payment-auth-required">
          <div className="w-14 h-14 mx-auto rounded-full bg-rose-100 flex items-center justify-center mb-4">
            <LogIn className="w-7 h-7 text-rose-500" />
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">Please log in to update your card</h1>
          <p className="text-sm text-gray-600 mb-6">
            For your security we need to verify your identity before opening the Stripe billing portal.
          </p>
          <button
            onClick={() => {
              const returnTo = encodeURIComponent('/subscription/update-payment');
              navigate(`/login?next=${returnTo}&reason=update_card`);
            }}
            data-testid="update-payment-login-btn"
            className="w-full bg-gradient-to-r from-rose-500 to-orange-500 hover:from-rose-600 hover:to-orange-600 text-white py-3 rounded-xl font-semibold flex items-center justify-center gap-2 shadow-md"
          >
            <LogIn className="w-4 h-4" />
            Log in to VirtualFit
          </button>
          <p className="text-xs text-gray-400 mt-4">
            {authLoading ? 'Checking your session…' : 'You&apos;ll be returned here right after.'}
          </p>
        </div>
      </div>
    );
  }

  const isPastDue = user?.subscription_status === 'past_due';
  const isSuspended = user?.subscription_status === 'suspended';
  const graceDays = user?.grace_days_left;

  return (
    <div className="min-h-[70vh] bg-gradient-to-br from-rose-50 to-orange-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center text-gray-600 hover:text-rose-500 mb-6 text-sm"
          data-testid="update-payment-back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to Dashboard
        </button>

        <div className="bg-white rounded-2xl shadow-xl p-8" data-testid="update-payment-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-rose-100 flex items-center justify-center">
              <CreditCard className="w-6 h-6 text-rose-500" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Update Payment Method</h1>
              <p className="text-sm text-gray-500">Secure card update via Stripe</p>
            </div>
          </div>

          {/* Status context */}
          {isPastDue && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6" data-testid="past-due-context">
              <div className="flex items-start">
                <AlertCircle className="w-5 h-5 text-amber-600 mr-2 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-800">
                  <strong>Payment failed on your last invoice.</strong>
                  {typeof graceDays === 'number' && (
                    <> You have <strong>{graceDays} day{graceDays === 1 ? '' : 's'}</strong> of full access remaining before your account is temporarily suspended.</>
                  )}
                  <div className="mt-2">
                    Update your card below and we'll instantly retry the payment — no waiting for Stripe's next scheduled retry.
                  </div>
                </div>
              </div>
            </div>
          )}
          {isSuspended && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6" data-testid="suspended-context">
              <div className="flex items-start">
                <AlertCircle className="w-5 h-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-red-800">
                  <strong>Your account is currently suspended.</strong> Try-on, portfolio, and collaborations are paused. The moment your card payment succeeds, full access is restored automatically — no admin action needed.
                </div>
              </div>
            </div>
          )}

          {/* Restored success state */}
          {status === 'restored' && user?.subscription_status === 'active' && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-6" data-testid="restored-state">
              <div className="flex items-start">
                <CheckCircle2 className="w-5 h-5 text-emerald-600 mr-2 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-emerald-800">
                  <strong>Payment successful — full access restored.</strong> You're all set.
                </div>
              </div>
              <button
                onClick={() => navigate('/dashboard')}
                className="mt-4 w-full bg-emerald-500 hover:bg-emerald-600 text-white py-3 rounded-xl font-semibold"
              >
                Continue to Dashboard
              </button>
            </div>
          )}

          {/* Restored but still processing */}
          {status === 'restored' && user?.subscription_status !== 'active' && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6" data-testid="restored-pending">
              <div className="flex items-start">
                <Loader2 className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5 animate-spin" />
                <div className="text-sm text-blue-800">
                  <strong>Payment submitted.</strong> Your access will be restored within a few seconds. Refresh this page shortly.
                </div>
              </div>
            </div>
          )}

          {/* Error state */}
          {status === 'error' && error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6" data-testid="update-payment-error">
              <p className="text-sm text-red-700"><strong>Error:</strong> {error}</p>
            </div>
          )}

          <div className="space-y-3">
            <p className="text-gray-600 text-sm leading-relaxed">
              We'll open Stripe's secure billing portal in a new page where you can add or replace your card.
              After you save the new card, we'll instantly attempt to pay the outstanding invoice — no waiting.
            </p>

            <button
              onClick={openBillingPortal}
              disabled={status === 'opening' || status === 'retrying'}
              data-testid="open-billing-portal-btn"
              className="w-full bg-gradient-to-r from-rose-500 to-orange-500 hover:from-rose-600 hover:to-orange-600 text-white py-3.5 rounded-xl font-semibold flex items-center justify-center gap-2 shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {status === 'opening' ? (
                <><Loader2 className="w-5 h-5 animate-spin" /> Opening Stripe…</>
              ) : status === 'retrying' ? (
                <><Loader2 className="w-5 h-5 animate-spin" /> Attempting Payment…</>
              ) : (
                <><CreditCard className="w-5 h-5" /> Update Card via Stripe</>
              )}
            </button>

            {/* Manual retry button if user is already in past_due/suspended with a card on file */}
            {(isPastDue || isSuspended) && status !== 'restored' && (
              <button
                onClick={retryOutstandingInvoice}
                disabled={status === 'retrying' || status === 'opening'}
                data-testid="retry-payment-btn"
                className="w-full bg-white border-2 border-gray-200 hover:border-rose-300 text-gray-700 py-3 rounded-xl font-medium disabled:opacity-50 text-sm"
              >
                {status === 'retrying' ? 'Retrying…' : 'Or retry existing card now'}
              </button>
            )}
          </div>

          <div className="mt-6 pt-6 border-t border-gray-100">
            <p className="text-xs text-gray-400 text-center">
              Payment updates are processed by Stripe. VirtualFit never sees your full card details.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
